import { useNavigate } from 'react-router-dom';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import type { GrantRole, Profile } from '@medtrack/shared';

import { cn } from '@/lib/cn';
import { useActiveProfile } from '@/lib/profile';
import { Avatar } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenuPrimitives as Menu } from '@/components/ui/dropdown-menu';

/**
 * Switch between the profiles this user has access to.
 *
 * The role is always visible, not hidden behind a hover: a caregiver looking at
 * someone else's medication needs to know at a glance whose screen they are on
 * and what they are allowed to do on it. Mistaking a viewer-only profile for
 * your own and wondering why "Take" is disabled is a small confusion; logging a
 * dose on the wrong person's profile is not.
 */

export const ROLE_LABEL: Record<GrantRole, string> = {
  owner: 'Owner',
  caregiver: 'Caregiver',
  viewer: 'View only',
};

/**
 * Query flag that asks the Settings page to open its "Add a profile" form on
 * arrival. Landing on /settings alone leaves the user hunting for the form
 * halfway down the page after tapping a button that promised to add a profile.
 * The page clears the flag once it has acted on it, so a refresh or a Back
 * does not pop the form open again.
 */
export const ADD_PROFILE_PARAM = 'addProfile';

export interface ProfileSwitcherProps {
  /**
   * 'menu'  — a button that opens a list. For the sidebar.
   * 'row'   — a horizontal row of avatars. For a mobile header, where a
   *           two-profile household should be one tap, not two.
   */
  variant?: 'menu' | 'row';
  /**
   * What "add profile" does. Defaults to navigating to /settings with the
   * form already open, since that page is where web-meds owns profile creation.
   */
  onAddProfile?: () => void;
  className?: string;
}

function ProfileRow({ profile }: { profile: Profile }): JSX.Element {
  return (
    <>
      <Avatar name={profile.displayName} colorHex={profile.colorHex} size="sm" />
      <span className="min-w-0 flex-1 text-left">
        <span className="block truncate text-sm font-medium text-ink">
          {profile.displayName}
        </span>
        <span className="block truncate text-xs text-muted">
          {ROLE_LABEL[profile.role]}
          {profile.isSelf ? ' · You' : ''}
        </span>
      </span>
    </>
  );
}

export function ProfileSwitcher({
  variant = 'menu',
  onAddProfile,
  className,
}: ProfileSwitcherProps): JSX.Element {
  const { profile, profiles, setProfileId, isLoading } = useActiveProfile();
  const navigate = useNavigate();

  const addProfile = (): void => {
    if (onAddProfile) onAddProfile();
    else navigate(`/settings?${ADD_PROFILE_PARAM}=1`);
  };

  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-3 p-2', className)}>
        <Skeleton circle className="size-8" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-2.5 w-16" />
        </div>
      </div>
    );
  }

  if (variant === 'row') {
    return (
      <div
        className={cn('flex items-center gap-2 overflow-x-auto', className)}
        role="group"
        aria-label="Switch profile"
      >
        {profiles.map((candidate) => {
          const active = candidate.id === profile?.id;
          return (
            <button
              key={candidate.id}
              type="button"
              onClick={() => setProfileId(candidate.id)}
              aria-pressed={active}
              aria-label={`${candidate.displayName} (${ROLE_LABEL[candidate.role]})`}
              className={cn(
                'relative flex size-11 shrink-0 items-center justify-center rounded-full',
                'transition-[box-shadow] duration-150 ease-out',
                'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                active
                  ? 'ring-2 ring-accent ring-offset-2 ring-offset-canvas'
                  : 'opacity-70 hover:opacity-100',
              )}
            >
              <Avatar
                name={candidate.displayName}
                colorHex={candidate.colorHex}
                size="sm"
              />
              {active ? (
                <span className="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-accent text-accent-ink">
                  <Check className="size-2.5" aria-hidden="true" />
                </span>
              ) : null}
            </button>
          );
        })}

        <button
          type="button"
          onClick={addProfile}
          aria-label="Add a profile"
          className={cn(
            'flex size-11 shrink-0 items-center justify-center rounded-full border border-dashed border-line',
            'text-muted transition-colors duration-150 ease-out hover:bg-raised hover:text-ink',
            'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          )}
        >
          <Plus className="size-4" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <Menu.Root>
      <Menu.Trigger
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-line bg-surface p-2 text-left',
          'min-h-14 transition-colors duration-150 ease-out hover:bg-raised',
          'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          'data-[state=open]:bg-raised',
          className,
        )}
        aria-label={
          profile
            ? `Current profile: ${profile.displayName}. Switch profile`
            : 'Switch profile'
        }
      >
        {profile ? (
          <ProfileRow profile={profile} />
        ) : (
          <span className="flex-1 text-sm text-muted">No profile</span>
        )}
        <ChevronsUpDown className="size-4 shrink-0 text-muted" aria-hidden="true" />
      </Menu.Trigger>

      <Menu.Portal>
        <Menu.Content
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className={cn(
            'z-50 min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-80',
            'overflow-hidden rounded-lg border border-line bg-surface p-1 shadow-pop',
            'data-[state=open]:animate-[medtrack-fade-in_150ms_ease-out]',
          )}
        >
          <Menu.Label className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted">
            Profiles
          </Menu.Label>

          {profiles.map((candidate) => (
            <Menu.Item
              key={candidate.id}
              onSelect={() => setProfileId(candidate.id)}
              className={cn(
                'flex min-h-11 cursor-pointer select-none items-center gap-3 rounded-md px-2 py-1.5 outline-none',
                'transition-colors duration-150 ease-out data-[highlighted]:bg-raised',
              )}
            >
              <ProfileRow profile={candidate} />
              {candidate.id === profile?.id ? (
                <Check className="size-4 shrink-0 text-accent" aria-hidden="true" />
              ) : null}
            </Menu.Item>
          ))}

          <Menu.Separator className="my-1 h-px bg-line" />

          <Menu.Item
            onSelect={addProfile}
            className={cn(
              'flex min-h-11 cursor-pointer select-none items-center gap-3 rounded-md px-2 py-1.5 text-sm outline-none',
              'text-ink transition-colors duration-150 ease-out data-[highlighted]:bg-raised',
            )}
          >
            <span className="flex size-8 items-center justify-center rounded-full border border-dashed border-line text-muted">
              <Plus className="size-4" aria-hidden="true" />
            </span>
            Add a profile
          </Menu.Item>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
